import React from 'react';
import { resolveTileClash } from '../../cfbBranding/resolveTileClash';
import type { TeamTileColors } from '../../cfbBranding/teamVisualResolver';

export type MatchupTileSize = 'hero' | 'rail' | 'compact' | 'band';

export type MatchupTileSide = {
  name: string;
  abbreviation: string;
  tileColors: TeamTileColors | null;
};

export type MatchupTileProps = {
  away: MatchupTileSide;
  home: MatchupTileSide;
  size?: MatchupTileSize;
  topLeft?: React.ReactNode;
  topRight?: React.ReactNode;
  bottomRight?: React.ReactNode;
  progress?: number | null;
};

const NEUTRAL_AWAY = '#222838';
const NEUTRAL_HOME = '#141821';
const NEUTRAL_INK = '#F2F4F8';

const SIZE_CLASS: Record<MatchupTileSize, string> = {
  hero: 'aspect-video w-full rounded-stream-lg text-5xl',
  rail: 'aspect-video w-72 rounded-stream-md text-4xl',
  compact: 'h-[54px] w-[96px] rounded-stream-sm text-sm',
  band: 'aspect-[16/7] max-h-[200px] w-full rounded-stream-md text-3xl',
};

function fill(colors: { fill: string } | null, neutral: string): string {
  return colors?.fill ?? neutral;
}

function ink(colors: { ink: string } | null): string {
  return colors?.ink ?? NEUTRAL_INK;
}

const MatchupTile: React.FC<MatchupTileProps> = ({
  away,
  home,
  size = 'rail',
  topLeft,
  topRight,
  bottomRight,
  progress,
}) => {
  const clamped =
    progress == null || Number.isNaN(progress) ? null : Math.min(1, Math.max(0, progress));
  const painted = resolveTileClash(away.tileColors, home.tileColors);

  return (
    <div
      role="img"
      aria-label={`${away.name} at ${home.name}`}
      data-testid="matchup-tile"
      data-tile-size={size}
      className={`relative overflow-hidden font-stream-display font-bold uppercase tracking-wide ${SIZE_CLASS[size]}`}
      style={{
        backgroundImage: `linear-gradient(115deg, ${fill(painted.away, NEUTRAL_AWAY)} 50%, ${fill(painted.home, NEUTRAL_HOME)} 50%)`,
      }}
    >
      <div className="absolute inset-0 flex items-center justify-between px-[8%]">
        <span style={{ color: ink(painted.away) }}>{away.abbreviation}</span>
        <span style={{ color: ink(painted.home) }}>{home.abbreviation}</span>
      </div>
      {topLeft ? (
        <div className="absolute left-2 top-2" data-testid="matchup-tile-top-left">
          {topLeft}
        </div>
      ) : null}
      {topRight ? (
        <div className="absolute right-2 top-2" data-testid="matchup-tile-top-right">
          {topRight}
        </div>
      ) : null}
      {bottomRight ? (
        <div className="absolute bottom-2 right-2" data-testid="matchup-tile-bottom-right">
          {bottomRight}
        </div>
      ) : null}
      {clamped != null ? (
        <div className="absolute inset-x-0 bottom-0 h-1 bg-black/30" data-testid="matchup-tile-progress">
          <div className="h-full bg-stream-live" style={{ width: `${clamped * 100}%` }} />
        </div>
      ) : null}
    </div>
  );
};

export default MatchupTile;
